import { useState } from "react";
import { Dialog } from "@mui/material";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { Box, HStack, VStack } from "../../components/utils";
import { CompanyDocument } from "../../types/UserDetailModel";
import { useRemoveCaptableSourceFile } from "../../queries/newCapTable/companyOnboarding";
import { queryClient } from "../../queries";
import { getAuthStore } from "../../store/useAuthStore";

type Props = {
  open: boolean;
  company: { uuid: string; nameOfTheCompany: string } | null;
  documents: CompanyDocument[];
  onClose: () => void;
  onReupload: () => void;
};

export default function RemoveCaptableModal({
  open,
  company,
  documents,
  onClose,
  onReupload,
}: Props) {
  const rows = documents || [];
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const { mutateAsync: removeCaptableSourceFile, isLoading } =
    useRemoveCaptableSourceFile();

  const handleRemove = async (documentId: string) => {
    try {
      await removeCaptableSourceFile({ documentId });
      toast("Cap table removed successfully", {
        type: "success",
        autoClose: 2000,
      });
      // Refresh everything cached app-wide, same as switching company —
      // the removed source file can affect data across the whole app, not
      // just this table's userCompanies list.
      await queryClient.invalidateQueries();
      queryClient.refetchQueries("userCompanies");

      // userContext is only fetched once at login and isn't re-triggered by
      // invalidateQueries above, so if the captable we just wiped belongs to
      // the currently active company, patch the cached flag directly —
      // otherwise the sidebar/route guards keep treating it as fully onboarded.
      const authStore = getAuthStore();
      if (company && authStore.companyId === company.uuid && authStore.user) {
        authStore.setAuth({
          user: { ...authStore.user, isDraftCompany: true },
        });
        queryClient.refetchQueries("userContext");
      }

      setConfirmingId(null);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.errorMessage ||
        error?.message ||
        "Failed to remove cap table";
      toast(errorMessage, { type: "error", autoClose: 4000 });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box className="p-5">
        <HStack className="items-center justify-between pb-1">
          <VStack className="gap-0.5">
            <p className="text-base font-semibold text-stone-800">
              Remove Cap Table
            </p>
            {company && (
              <p className="text-xs text-stone-400">
                {company.nameOfTheCompany}
              </p>
            )}
          </VStack>
          <XMarkIcon
            className="h-6 w-6 shrink-0 cursor-pointer text-stone-500 hover:text-stone-700"
            onClick={onClose}
          />
        </HStack>

        <VStack className="gap-2.5 pt-4 h-[250px] overflow-y-auto pr-1">
          {rows.length === 0 && (
            <VStack className="h-[150px] items-center justify-center gap-2">
              <Icon
                icon="mdi:file-search-outline"
                height={32}
                className="text-stone-300"
              />
              <p className="text-sm text-stone-400">
                No cap table source file found for this company
              </p>
              <button
                type="button"
                onClick={onReupload}
                className="text-sm font-medium text-primary hover:underline"
              >
                Attach a cap table
              </button>
            </VStack>
          )}

          {rows.map((doc) => (
            <HStack
              key={doc.id}
              className="group items-center justify-between rounded-xl border border-stone-200 bg-white px-3.5 py-3 transition hover:border-stone-300 hover:shadow-sm"
            >
              <HStack className="items-center gap-3 min-w-0">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-green-50">
                  <Icon
                    icon="mdi:file-excel-outline"
                    height={22}
                    className="text-green-600"
                  />
                </div>
                <VStack className="min-w-0 gap-0.5">
                  <p
                    className="text-sm font-medium text-stone-700 truncate max-w-xs"
                    title={doc.name}
                  >
                    {doc.name}
                  </p>
                </VStack>
              </HStack>

              {confirmingId === doc.id ? (
                <HStack className="items-center gap-2 shrink-0">
                  <button
                    type="button"
                    disabled={isLoading}
                    onClick={() => handleRemove(doc.id)}
                    className="rounded-md bg-red-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    {isLoading ? "Removing…" : "Confirm"}
                  </button>
                  <button
                    type="button"
                    disabled={isLoading}
                    onClick={() => setConfirmingId(null)}
                    className="rounded-md border border-stone-200 px-2.5 py-1 text-xs font-medium text-stone-600 hover:bg-stone-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </HStack>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmingId(doc.id)}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-stone-400 transition hover:bg-red-50 hover:text-red-600"
                >
                  <Icon icon="mdi:trash-can-outline" height={19} />
                </button>
              )}
            </HStack>
          ))}
        </VStack>
      </Box>
    </Dialog>
  );
}
